import { input } from './data.js';
import { clamp } from './math.js';
export const KEYMAPS = [
  { left: 'KeyA', right: 'KeyD', up: 'KeyW', down: 'KeyS', jump: 'Space', attack: 'KeyJ', heavy: 'KeyK', special: 'KeyL', shield: 'ShiftLeft', dodge: 'KeyE' },
  { left: 'ArrowLeft', right: 'ArrowRight', up: 'ArrowUp', down: 'ArrowDown', jump: 'KeyN', attack: 'Comma', heavy: 'Period', special: 'Slash', shield: 'ShiftRight', dodge: 'KeyM' },
];
const ACTIONS = ['jump', 'attack', 'heavy', 'special', 'dodge'];
const DEAD_ZONE = 0.18;
const axis = x => Math.abs(x || 0) < DEAD_ZONE ? 0 : clamp((Math.abs(x) - DEAD_ZONE) / (1 - DEAD_ZONE), 0, 1) * Math.sign(x);
export const isEditing = target => target instanceof Element && Boolean(target.closest('input, select, textarea, [contenteditable="true"], dialog'));
export class InputManager {
  constructor(touchRoot) {
    this.keys = new Set(); this.edges = new Set(); this.touch = new Map(); this.touchEdges = new Set();
    this.gamepadPrevious = [input(), input()]; this.gamepadEdges = [new Set(), new Set()];
    this.gamepadState = [input(), input()]; this.gamepadCount = 0; this.local = false;
    this.activePointers = new Map(); this.abort = new AbortController();
    const options = { signal: this.abort.signal };
    const known = new Set(KEYMAPS.flatMap(m => Object.values(m)));
    window.addEventListener('keydown', e => {
      if (isEditing(e.target)) return;
      if (known.has(e.code)) e.preventDefault();
      if (!this.keys.has(e.code)) this.edges.add(e.code);
      this.keys.add(e.code);
    }, options);
    window.addEventListener('keyup', e => { this.keys.delete(e.code); }, options);
    window.addEventListener('blur', () => this.clear(), options);
    document.addEventListener('visibilitychange', () => { if (document.hidden) this.clear(); }, options);
    if (touchRoot) {
      touchRoot.addEventListener('pointerdown', e => {
        const button = e.target.closest('[data-action]');
        if (!button) return;
        e.preventDefault(); button.setPointerCapture(e.pointerId);
        const action = button.dataset.action;
        this.activePointers.set(e.pointerId, action);
        if (!this.touch.has(action)) this.touchEdges.add(action);
        this.touch.set(action, (this.touch.get(action) || 0) + 1); button.classList.add('held');
      }, options);
      const release = e => {
        const action = this.activePointers.get(e.pointerId); if (!action) return;
        this.activePointers.delete(e.pointerId);
        const remaining = (this.touch.get(action) || 1) - 1;
        if (remaining > 0) this.touch.set(action, remaining); else this.touch.delete(action);
        touchRoot.querySelector(`[data-action="${action}"]`)?.classList.toggle('held', remaining > 0);
      };
      touchRoot.addEventListener('pointerup', release, options);
      touchRoot.addEventListener('pointercancel', release, options);
      touchRoot.addEventListener('lostpointercapture', release, options);
    }
  }
  /** Retain gamepad press edges until a simulation tick consumes them. */
  pollGamepads() {
    const pads = typeof navigator.getGamepads === 'function' ? Array.from(navigator.getGamepads()).filter(p => p && p.connected) : [];
    this.gamepadCount = pads.length;
    for (let slot = 0; slot < 2; slot++) {
      const pad = this.local && pads.length === 1 ? (slot === 1 ? pads[0] : undefined) : pads[slot];
      const c = input();
      if (pad) {
        const b = index => Boolean(pad.buttons[index]?.pressed);
        c.x = axis(pad.axes[0]) || Number(b(15)) - Number(b(14));
        c.y = -axis(pad.axes[1]) || Number(b(12)) - Number(b(13));
        c.jump = c.jumpHeld = b(0); c.attack = b(2); c.heavy = c.heavyHeld = b(3); c.special = b(1);
        c.shield = b(6) || b(7); c.dodge = b(4) || b(5);
      }
      for (const action of ACTIONS) if (c[action] && !this.gamepadPrevious[slot][action]) this.gamepadEdges[slot].add(action);
      this.gamepadPrevious[slot] = { ...c }; this.gamepadState[slot] = c;
    }
  }
  sample(slot) {
    const map = KEYMAPS[slot], pad = this.gamepadState[slot], c = input();
    const held = action => this.keys.has(map[action]) || (slot === 0 && this.touch.has(action));
    c.x = clamp(Number(held('right')) - Number(held('left')) + pad.x, -1, 1);
    c.y = clamp(Number(held('up')) - Number(held('down')) + pad.y, -1, 1);
    for (const action of ACTIONS) {
      c[action] = this.edges.has(map[action]) || this.gamepadEdges[slot].has(action) || (slot === 0 && this.touchEdges.has(action));
      this.edges.delete(map[action]); this.gamepadEdges[slot].delete(action);
    }
    c.jumpHeld = held('jump') || pad.jumpHeld; c.heavyHeld = held('heavy') || pad.heavyHeld; c.shield = held('shield') || pad.shield;
    if (slot === 0) this.touchEdges.clear(); return c;
  }
  clear() {
    this.keys.clear(); this.edges.clear(); this.touch.clear(); this.touchEdges.clear(); this.activePointers.clear();
    this.gamepadEdges.forEach(s => s.clear());
    document.querySelectorAll('[data-action].held').forEach(e => e.classList.remove('held'));
  }
  destroy() { this.abort.abort(); this.clear(); }
}
